/* ============================================================================
   03 · Collection grid stagger
   ----------------------------------------------------------------------------
   Pattern: ScrollTrigger.batch — cards that enter the viewport together animate
   together, as a row, with a tight stagger inside the row.

   Why batch and not one trigger per card: with 60 products, per-card triggers
   produce a long serial queue where the bottom of a row is still animating
   while you've scrolled past it. Batch groups whatever crosses the threshold
   in the same frame, so the grid reads row by row — which is how people
   actually scan a PLP.

   What's being decided here:
     • Stagger tightness. stagger.tight (40ms) across a 4-up row = 120ms from
       first to last card. Push to stagger.base and the row starts to feel like
       a wave, which fights scanning.
     • Whether cards animate at all on re-scroll. They don't — `once: true`.
       Re-animating on every upward scroll is the single most common reason a
       PLP feels busy.
     • Hover: the alt-image cross-fade is opacity-only and the card scale is
       subtle (1.04). Anything more and the grid becomes noisy in aggregate,
       because a shopper's pointer sweeps across many cards per second.

   Nothing here is scroll-scrubbed, so the lab's Speed control works on all of
   it — drag to 0.2x and watch the row resolve.
   ========================================================================= */

import { initMotion, refreshOnSettle, isReduced, MOTION } from '../../src/motion.js';
import { mountLabBar } from '../../src/lab.js';

const { gsap, ScrollTrigger } = initMotion();
mountLabBar({ title: '03 · Grid stagger' });
refreshOnSettle();

/* --- Placeholder catalogue -------------------------------------------------
   Real product data (names, prices, colorways, hero + alt imagery) can be
   pulled from wearfigs.com rather than invented. Swap this array for that
   payload when the grid needs to be shown to stakeholders — placeholder
   gradients are fine for judging motion, not for judging the design. */
const COLORWAYS = [
  ['--p-navy', '--p-royal-blue', '--p-graphite'],
  ['--p-burgundy', '--p-mauve', '--p-espresso'],
  ['--p-caribbean-blue', '--p-teal', '--p-hunter-green'],
  ['--p-black', '--p-charcoal', '--p-graphite'],
  ['--p-sandstone', '--p-espresso', '--p-graphite'],
  ['--p-surgical-green', '--p-hunter-green', '--p-teal'],
  ['--p-lavender-dew', '--p-mauve', '--p-graphite'],
  ['--p-ceil-blue', '--p-royal-blue', '--p-navy'],
];

const NAMES = [
  'Catarina One-Pocket Top',
  'Casma Three-Pocket Top',
  'Rafaela Oversized Top',
  'Leon Two-Pocket Top',
  'Zamora Jogger Scrub Pant',
  'Livingston Basic Scrub Pant',
  'Kade Cargo Scrub Pant',
  'Vienna Modern-Fit Top',
  'Yola Skinny Scrub Pant',
  'Delta Half-Zip Pullover',
  'Marlowe Zip Jacket',
  'On-Shift Short Sleeve Tee',
];

const grid = document.querySelector('[data-grid]');

grid.innerHTML = NAMES.map((name, i) => {
  const palette = COLORWAYS[i % COLORWAYS.length];
  const price = 38 + ((i * 7) % 5) * 6;
  const isNew = i % 5 === 0;

  return `
    <article class="card" data-card>
      <div class="card__media">
        ${isNew ? '<span class="card__badge">New</span>' : ''}
        <div class="card__image" style="background:linear-gradient(155deg, var(${palette[0]}), var(${palette[1]}) 80%)"></div>
        <div class="card__image card__image--alt" style="background:linear-gradient(200deg, var(${palette[1]}), var(${palette[2]}) 85%)"></div>
        <button class="card__quick" type="button">Quick add</button>
      </div>
      <div class="card__info">
        <h2 class="card__name">${name}</h2>
        <p class="card__price">$${price}</p>
        <div class="card__swatches">
          ${palette
            .map(
              (c, ci) =>
                `<button class="card__swatch" type="button" aria-label="Colorway ${ci + 1}" aria-pressed="${ci === 0}" style="background:var(${c})"></button>`
            )
            .join('')}
        </div>
      </div>
    </article>`;
}).join('');

/* --- Swatch selection ------------------------------------------------------
   Micro-interaction worth getting right: the swatch scales with a spring while
   the card image cross-fades. Spring is reserved for affirmative, user-initiated
   moments like this — never for content arriving on its own. */
grid.addEventListener('click', (e) => {
  const swatch = e.target.closest('.card__swatch');
  if (!swatch) return;

  const card = swatch.closest('[data-card]');
  card.querySelectorAll('.card__swatch').forEach((s) => s.setAttribute('aria-pressed', 'false'));
  swatch.setAttribute('aria-pressed', 'true');

  const primary = card.querySelector('.card__image:not(.card__image--alt)');
  const target = getComputedStyle(swatch).backgroundColor;

  gsap
    .timeline()
    .to(primary, {
      opacity: 0,
      duration: MOTION.dur.quick,
      ease: MOTION.ease.exit,
    })
    .set(primary, { background: `linear-gradient(155deg, ${target}, ${target})` })
    .to(primary, {
      opacity: 1,
      duration: MOTION.dur.base,
      ease: MOTION.ease.entrance,
    });
});

/* --- Reveal ---------------------------------------------------------------- */
let batches = [];

function build() {
  batches.forEach((b) => b.kill?.());
  batches = [];
  ScrollTrigger?.getAll().forEach((t) => t.kill());

  const cards = gsap.utils.toArray('[data-card]');
  const heads = gsap.utils.toArray('[data-head]');

  if (isReduced()) {
    gsap.set([...cards, ...heads], { opacity: 1, y: 0, clearProps: 'transform' });
    return;
  }

  gsap.set(cards, { opacity: 0, y: MOTION.rise.md });
  gsap.set(heads, { opacity: 0, y: MOTION.rise.sm });

  gsap.to(heads, {
    opacity: 1,
    y: 0,
    duration: MOTION.dur.slow,
    stagger: MOTION.stagger.base,
  });

  batches = ScrollTrigger.batch(cards, {
    // 88% = fire just before the card is fully on screen. Trigger at 100% and
    // the animation happens off-screen; at 60% the card is already read before
    // it moves, which looks like a bug.
    start: 'top 88%',
    once: true,
    batchMax: 6,
    onEnter: (group) =>
      gsap.to(group, {
        opacity: 1,
        y: 0,
        duration: MOTION.dur.slow,
        ease: MOTION.ease.entrance,
        stagger: MOTION.stagger.tight,
        overwrite: true,
      }),
  });
}

build();
window.addEventListener('mo:replay', build);
